import { createContext, useCallback, useContext, useState, useEffect } from 'react';
import { clearSessionExpiredPopup, getMe, logoutSession } from '../api/api';
import { clearAdminAccessCache } from '../hooks/useAdminAccess';

export const AuthContext = createContext();

const clearStoredAuth = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('cached_user');
  localStorage.removeItem('cached_stats');
  localStorage.removeItem('cached_inventory');
  localStorage.removeItem('cached_equipped');
  clearAdminAccessCache();
};

const cacheSession = (user, stats = null) => {
  localStorage.setItem('cached_user', JSON.stringify(user));
  if (stats) {
    localStorage.setItem('cached_stats', JSON.stringify(stats));
  } else {
    localStorage.removeItem('cached_stats');
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const cached = localStorage.getItem('cached_user');
    return cached ? JSON.parse(cached) : null;
  });
  const [stats, setStats] = useState(() => {
    const cached = localStorage.getItem('cached_stats');
    return cached ? JSON.parse(cached) : null;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getMe({ skipSessionExpiredPopup: true })
      .then(res => {
        setUser(res.data.user);
        setStats(res.data.stats);
        cacheSession(res.data.user, res.data.stats);
      })
      .catch(() => {
        clearStoredAuth();
        clearSessionExpiredPopup();
        setUser(null);
        setStats(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const loginUser = useCallback(async (userData) => {
    localStorage.removeItem('token');
    clearSessionExpiredPopup();

    try {
      const res = await getMe({ skipSessionExpiredPopup: true });
      setUser(res.data.user);
      setStats(res.data.stats);
      cacheSession(res.data.user, res.data.stats);
    } catch {
      if (!userData?.user_id && !userData?.username) {
        clearStoredAuth();
        setUser(null);
        setStats(null);
        throw new Error('Session unavailable');
      }
      const fallbackUser = {
        id: userData?.user_id,
        username: userData?.username,
        ...userData,
      };
      setUser(fallbackUser);
      setStats(null);
      cacheSession(fallbackUser);
    }
  }, []);

  const logoutUser = useCallback(async () => {
    try {
      await logoutSession();
    } catch {
      // Local cleanup still logs the user out if the network request fails.
    }
    clearStoredAuth();
    clearSessionExpiredPopup();
    setUser(null);
    setStats(null);
  }, []);

  const logout = logoutUser;

  return (
    <AuthContext.Provider value={{ user, stats, loading, loginUser, logoutUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);